import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";

import BrandLogoLinks from "../Components/Common/BrandLogoLinks";
import LanguageDropdown from "../Components/Common/LanguageDropdown";
import FullScreenDropdown from "../Components/Common/FullScreenDropdown";
import ProfileDropdown from "../Components/Common/ProfileDropdown";
import LightDark from "../Components/Common/LightDark";

import { helpUrlForRoute } from "../constants/helpRouteMap";
import { changeSidebarVisibility } from "../slices/thunks";
import {
  readAlwaysIconsOnly,
  subscribeAlwaysIconsOnly,
  toggleAlwaysIconsOnly,
} from "../helpers/twoColumnAlwaysIcons";

/**
 * Header tenant enxuto (LAC-011 / DOC 09 § 4.1).
 * Removidos demos Velzon: Search, Cart, WebApps, NotificationDropdown.
 * Mantém: logo, hamburger, preferência só-ícones (LAC-190), idioma, fullscreen, tema, ajuda (?), perfil.
 */
const Header = ({ onChangeLayoutMode, layoutModeType, headerClass }: any) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const helpHref = helpUrlForRoute(location.pathname, i18n.language);
  const dispatch: any = useDispatch();
  const [alwaysIcons, setAlwaysIcons] = useState(() =>
    readAlwaysIconsOnly("tenant")
  );

  useEffect(() => {
    return subscribeAlwaysIconsOnly(() => {
      setAlwaysIcons(readAlwaysIconsOnly("tenant"));
    });
  }, []);

  const selectDashboardData = createSelector(
    (state) => state.Layout,
    (sidebarVisibilitytype) => sidebarVisibilitytype.sidebarVisibilitytype
  );
  const sidebarVisibilitytype = useSelector(selectDashboardData);

  const toogleMenuBtn = () => {
    var windowSize = document.documentElement.clientWidth;
    const humberIcon = document.querySelector(".hamburger-icon") as HTMLElement;
    dispatch(changeSidebarVisibility("show"));

    if (windowSize > 767) humberIcon.classList.toggle("open");

    //For collapse horizontal menu
    if (document.documentElement.getAttribute("data-layout") === "horizontal") {
      document.body.classList.contains("menu")
        ? document.body.classList.remove("menu")
        : document.body.classList.add("menu");
    }

    //For collapse vertical and semibox menu
    if (
      sidebarVisibilitytype === "show" &&
      (document.documentElement.getAttribute("data-layout") === "vertical" ||
        document.documentElement.getAttribute("data-layout") === "semibox")
    ) {
      if (windowSize < 1025 && windowSize > 767) {
        document.body.classList.remove("vertical-sidebar-enable");
        document.documentElement.getAttribute("data-sidebar-size") === "sm"
          ? document.documentElement.setAttribute("data-sidebar-size", "")
          : document.documentElement.setAttribute("data-sidebar-size", "sm");
      } else if (windowSize > 1025) {
        document.body.classList.remove("vertical-sidebar-enable");
        document.documentElement.getAttribute("data-sidebar-size") === "lg"
          ? document.documentElement.setAttribute("data-sidebar-size", "sm")
          : document.documentElement.setAttribute("data-sidebar-size", "lg");
      } else if (windowSize <= 767) {
        document.body.classList.add("vertical-sidebar-enable");
        document.documentElement.setAttribute("data-sidebar-size", "lg");
      }
    }

    // Two column: toggle temporário da 2ª coluna (preferência always-icons é outro botão).
    if (document.documentElement.getAttribute("data-layout") === "twocolumn") {
      document.body.classList.contains("twocolumn-panel")
        ? document.body.classList.remove("twocolumn-panel")
        : document.body.classList.add("twocolumn-panel");
    }
  };

  const onToggleAlwaysIcons = () => {
    toggleAlwaysIconsOnly("tenant");
  };

  return (
    <React.Fragment>
      <header id="page-topbar" className={headerClass}>
        <div className="layout-width">
          <div className="navbar-header">
            <div className="d-flex">
              <BrandLogoLinks to="/" />

              <button
                onClick={toogleMenuBtn}
                type="button"
                className="btn btn-sm px-3 fs-16 header-item vertical-menu-btn topnav-hamburger"
                id="topnav-hamburger-icon"
                title={
                  alwaysIcons
                    ? t("shell.twoColumn.hamburgerAlwaysOn")
                    : t("shell.twoColumn.hamburger")
                }
                aria-label={
                  alwaysIcons
                    ? t("shell.twoColumn.hamburgerAlwaysOn")
                    : t("shell.twoColumn.hamburger")
                }
              >
                <span className="hamburger-icon">
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              </button>

              {/* Preferência persistente — hamburger só abre/fecha a 2ª coluna nesta tela. */}
              <button
                type="button"
                className={`btn btn-icon btn-topbar btn-ghost-secondary rounded-circle d-none d-md-inline-flex ${
                  alwaysIcons ? "text-primary" : ""
                }`}
                onClick={onToggleAlwaysIcons}
                title={
                  alwaysIcons
                    ? t("shell.twoColumn.alwaysIconsOn")
                    : t("shell.twoColumn.alwaysIconsOff")
                }
                aria-label={
                  alwaysIcons
                    ? t("shell.twoColumn.alwaysIconsOn")
                    : t("shell.twoColumn.alwaysIconsOff")
                }
                aria-pressed={alwaysIcons}
              >
                <i className="bx bx-sidebar fs-22" aria-hidden="true"></i>
              </button>
            </div>

            <div className="d-flex align-items-center">
              <LanguageDropdown />

              <FullScreenDropdown />

              <LightDark
                layoutMode={layoutModeType}
                onChangeLayoutMode={onChangeLayoutMode}
              />

              {/* Ajuda contextual pela rota atual (DOC 17 · helpRouteMap) */}
              <div className="ms-1 header-item d-none d-sm-flex">
                <a
                  href={helpHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle"
                  title={t("menu.help")}
                  aria-label={t("menu.help")}
                >
                  <i className="bx bx-help-circle fs-22"></i>
                </a>
              </div>

              <ProfileDropdown />
            </div>
          </div>
        </div>
      </header>
    </React.Fragment>
  );
};

export default Header;
